/**
 * Admin Dashboard - Hidden at /admin
 * Shows search analytics, error logs, and submission review.
 */
import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  updateDoc,
  doc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

type Tab = 'searches' | 'errors' | 'submissions' | 'imports';

interface SearchLog {
  id: string;
  query: string;
  lat?: number;
  lng?: number;
  resultCount: number;
  deviceType: string;
  timestamp: Timestamp;
  userId?: string;
  isAnonymous?: boolean;
}

interface ErrorLog {
  id: string;
  message: string;
  stack?: string;
  correlationId?: string;
  searchQuery?: string;
  component?: string;
  timestamp: Timestamp;
  url?: string;
}

interface Submission {
  id: string;
  lat: number;
  lng: number;
  name?: string;
  address?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'rejected';
  submittedBy: string;
  submittedAt: Timestamp;
  reviewedAt?: Timestamp;
}

interface ImportLog {
  id: string;
  countryCode: string;
  totalParkings: number;
  withHeight: number;
  duration: number;
  success: boolean;
  error?: string;
  importedAt: Timestamp;
}

function formatDate(ts: Timestamp | undefined): string {
  if (!ts) return '-';
  return ts.toDate().toLocaleString();
}

function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
        active
          ? 'bg-card border-t border-l border-r border-rule text-ink1'
          : 'bg-transparent text-ink3 hover:text-ink2'
      }`}
    >
      {children}
    </button>
  );
}

function SearchesTab() {
  const [logs, setLogs] = useState<SearchLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const q = query(
          collection(db, 'searchLogs'),
          orderBy('timestamp', 'desc'),
          limit(100)
        );
        const snap = await getDocs(q);
        setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() } as SearchLog)));
      } catch (err) {
        console.error('Failed to load search logs:', err);
      }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div className="p-4 text-ink3">Loading...</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-tint">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-ink2">Time</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Query</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Location</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Results</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Device</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">User</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id} className="border-b border-rule hover:bg-tint/50">
              <td className="px-3 py-2 text-ink3 whitespace-nowrap">
                {formatDate(log.timestamp)}
              </td>
              <td className="px-3 py-2 text-ink1 max-w-[200px] truncate">{log.query}</td>
              <td className="px-3 py-2 text-ink3 text-xs">
                {log.lat && log.lng ? `${log.lat.toFixed(4)}, ${log.lng.toFixed(4)}` : '-'}
              </td>
              <td className="px-3 py-2 text-ink1">{log.resultCount}</td>
              <td className="px-3 py-2 text-ink3">{log.deviceType}</td>
              <td className="px-3 py-2 text-ink3 text-xs">
                {log.isAnonymous ? 'anon' : log.userId?.slice(0, 8) || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {logs.length === 0 && (
        <div className="p-8 text-center text-ink3">No search logs yet</div>
      )}
    </div>
  );
}

function ErrorsTab() {
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const q = query(
          collection(db, 'errorLogs'),
          orderBy('timestamp', 'desc'),
          limit(100)
        );
        const snap = await getDocs(q);
        setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() } as ErrorLog)));
      } catch (err) {
        console.error('Failed to load error logs:', err);
      }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div className="p-4 text-ink3">Loading...</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-tint">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-ink2">Time</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Message</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Component</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Correlation ID</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">URL</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id} className="border-b border-rule hover:bg-tint/50">
              <td className="px-3 py-2 text-ink3 whitespace-nowrap">
                {formatDate(log.timestamp)}
              </td>
              <td className="px-3 py-2 text-red-600 max-w-[300px] truncate">{log.message}</td>
              <td className="px-3 py-2 text-ink3">{log.component || '-'}</td>
              <td className="px-3 py-2 text-ink3 text-xs font-mono">
                {log.correlationId || '-'}
              </td>
              <td className="px-3 py-2 text-ink3 text-xs max-w-[150px] truncate">
                {log.url || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {logs.length === 0 && (
        <div className="p-8 text-center text-ink3">No errors logged 🎉</div>
      )}
    </div>
  );
}

function SubmissionsTab() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const q = query(
          collection(db, 'submissions'),
          orderBy('submittedAt', 'desc'),
          limit(100)
        );
        const snap = await getDocs(q);
        setSubmissions(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Submission)));
      } catch (err) {
        console.error('Failed to load submissions:', err);
      }
      setLoading(false);
    }
    load();
  }, []);

  const handleApprove = async (id: string) => {
    await updateDoc(doc(db, 'submissions', id), {
      status: 'approved',
      reviewedAt: Timestamp.now(),
    });
    setSubmissions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: 'approved' } : s))
    );
  };

  const handleReject = async (id: string) => {
    await updateDoc(doc(db, 'submissions', id), {
      status: 'rejected',
      reviewedAt: Timestamp.now(),
    });
    setSubmissions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: 'rejected' } : s))
    );
  };

  if (loading) return <div className="p-4 text-ink3">Loading...</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-tint">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-ink2">Time</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Location</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Name</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Notes</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Status</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((sub) => (
            <tr key={sub.id} className="border-b border-rule hover:bg-tint/50">
              <td className="px-3 py-2 text-ink3 whitespace-nowrap">
                {formatDate(sub.submittedAt)}
              </td>
              <td className="px-3 py-2 text-ink3 text-xs">
                <a
                  href={`https://www.google.com/maps?q=${sub.lat},${sub.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {sub.lat.toFixed(4)}, {sub.lng.toFixed(4)}
                </a>
              </td>
              <td className="px-3 py-2 text-ink1">{sub.name || '-'}</td>
              <td className="px-3 py-2 text-ink3 max-w-[200px] truncate">
                {sub.notes || '-'}
              </td>
              <td className="px-3 py-2">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    sub.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : sub.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                  }`}
                >
                  {sub.status}
                </span>
              </td>
              <td className="px-3 py-2">
                {sub.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(sub.id)}
                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(sub.id)}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {submissions.length === 0 && (
        <div className="p-8 text-center text-ink3">No submissions yet</div>
      )}
    </div>
  );
}

function ImportsTab() {
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const q = query(
          collection(db, 'importLogs'),
          orderBy('importedAt', 'desc'),
          limit(100)
        );
        const snap = await getDocs(q);
        setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() } as ImportLog)));
      } catch (err) {
        console.error('Failed to load import logs:', err);
      }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div className="p-4 text-ink3">Loading...</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-tint">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-ink2">Time</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Country</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Parkings</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">With Height</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Duration</th>
            <th className="px-3 py-2 text-left font-medium text-ink2">Status</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id} className="border-b border-rule hover:bg-tint/50">
              <td className="px-3 py-2 text-ink3 whitespace-nowrap">
                {formatDate(log.importedAt)}
              </td>
              <td className="px-3 py-2 text-ink1 font-medium">{log.countryCode}</td>
              <td className="px-3 py-2 text-ink1">{log.totalParkings.toLocaleString()}</td>
              <td className="px-3 py-2 text-ink3">
                {log.withHeight.toLocaleString()} (
                {Math.round((log.withHeight / log.totalParkings) * 100)}%)
              </td>
              <td className="px-3 py-2 text-ink3">{formatDuration(log.duration)}</td>
              <td className="px-3 py-2">
                {log.success ? (
                  <span className="text-green-600">✓ Success</span>
                ) : (
                  <span className="text-red-600" title={log.error}>
                    ✗ Failed
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {logs.length === 0 && (
        <div className="p-8 text-center text-ink3">No import logs yet</div>
      )}
    </div>
  );
}

export function AdminPage() {
  const { firebaseUser, ready } = useAuth();
  const [tab, setTab] = useState<Tab>('searches');

  if (!ready) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-ink3">Loading...</div>
      </div>
    );
  }

  // Check admin access
  const isAdmin = firebaseUser?.email === 'romy.abbrederis@gmail.com';
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-ink1 mb-2">Access Denied</h1>
          <p className="text-ink3">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-ink1 mb-6">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-rule">
          <TabButton active={tab === 'searches'} onClick={() => setTab('searches')}>
            Searches
          </TabButton>
          <TabButton active={tab === 'errors'} onClick={() => setTab('errors')}>
            Errors
          </TabButton>
          <TabButton active={tab === 'submissions'} onClick={() => setTab('submissions')}>
            Submissions
          </TabButton>
          <TabButton active={tab === 'imports'} onClick={() => setTab('imports')}>
            Imports
          </TabButton>
        </div>

        {/* Tab content */}
        <div className="bg-card border border-t-0 border-rule rounded-b-lg overflow-hidden">
          {tab === 'searches' && <SearchesTab />}
          {tab === 'errors' && <ErrorsTab />}
          {tab === 'submissions' && <SubmissionsTab />}
          {tab === 'imports' && <ImportsTab />}
        </div>
      </div>
    </div>
  );
}
